/**
 * 认证服务模块
 * 
 * @description 提供用户认证相关的API调用，包括登录、注册、登出等功能
 * @author Cross-Border ERP Team
 * @version 1.0.0
 */

import { get, post } from './request';
import type { ApiResponse } from './typings';
import type {
  LoginParams,
  RegisterParams,
  LoginResponseData,
  RegisterResponseData,
  UserInfoResponseData,
  CurrentUser,
} from './auth.d';

// ============= 登录相关API =============

/**
 * 用户登录
 * 
 * @description 验证用户凭据并返回访问令牌
 * @param params 登录参数
 * @returns Promise<ApiResponse<LoginResponseData>>
 * 
 * @example
 * ```typescript
 * const result = await login({
 *   email: 'user@example.com',
 *   password: 'password123'
 * });
 * 
 * if (result.success) {
 *   console.log('登录成功:', result.data.user);
 *   localStorage.setItem('token', result.data.token);
 * }
 * ```
 */
export async function login(params: LoginParams): Promise<ApiResponse<LoginResponseData>> {
  return post<LoginResponseData>('/api/auth/login', params);
}

/**
 * 用户注册
 * 
 * @description 创建新用户账户并返回访问令牌
 * @param params 注册参数
 * @returns Promise<ApiResponse<RegisterResponseData>>
 * 
 * @example
 * ```typescript
 * const result = await register({
 *   name: '张三',
 *   email: 'zhangsan@example.com',
 *   password: 'password123',
 *   password_confirmation: 'password123'
 * });
 * 
 * if (result.success) {
 *   console.log('注册成功:', result.data.user);
 * }
 * ```
 */
export async function register(params: RegisterParams): Promise<ApiResponse<RegisterResponseData>> {
  return post<RegisterResponseData>('/api/auth/register', params);
}

// ============= 用户信息相关API =============

/**
 * 获取当前用户信息
 * 
 * @description 返回当前认证用户的详细信息
 * @returns Promise<ApiResponse<CurrentUser>>
 * 
 * @example
 * ```typescript
 * const result = await getCurrentUser();
 * 
 * if (result.success) {
 *   console.log('用户信息:', result.data);
 * }
 * ```
 */
export async function getCurrentUser(): Promise<ApiResponse<CurrentUser>> {
  return get<CurrentUser>('/api/auth/user');
}

/**
 * 获取用户详细信息（兼容性方法）
 * 
 * @description 获取当前用户信息的包装方法，返回格式与旧版本兼容
 * @returns Promise<ApiResponse<UserInfoResponseData>>
 */
export async function getUserInfo(): Promise<ApiResponse<UserInfoResponseData>> {
  const response = await getCurrentUser();
  
  // 转换为兼容格式
  return {
    ...response,
    data: {
      user: response.data
    }
  };
}

// ============= 登出相关API =============

/**
 * 用户登出
 * 
 * @description 删除当前设备的访问令牌，使其失效
 * @returns Promise<ApiResponse<null>>
 * 
 * @example
 * ```typescript
 * const result = await logout();
 * 
 * if (result.success) {
 *   console.log('登出成功');
 *   localStorage.removeItem('token');
 * }
 * ```
 */
export async function logout(): Promise<ApiResponse<null>> {
  return post<null>('/api/auth/logout');
}

/**
 * 从所有设备登出
 * 
 * @description 删除用户的所有访问令牌，强制从所有设备登出
 * @returns Promise<ApiResponse<null>>
 * 
 * @example
 * ```typescript
 * const result = await logoutAll();
 * 
 * if (result.success) {
 *   console.log('已从所有设备登出');
 * }
 * ```
 */
export async function logoutAll(): Promise<ApiResponse<null>> {
  return post<null>('/api/auth/logout-all');
}

// ============= 令牌管理相关 =============

/**
 * 刷新访问令牌
 * 
 * @description 使用刷新令牌获取新的访问令牌
 * @returns Promise<ApiResponse<LoginResponseData>>
 * 
 * @example
 * ```typescript
 * const result = await refreshToken();
 * 
 * if (result.success) {
 *   localStorage.setItem('token', result.data.token);
 * }
 * ```
 */
export async function refreshToken(): Promise<ApiResponse<LoginResponseData>> {
  return post<LoginResponseData>('/api/auth/refresh');
}

// ============= 密码相关API =============

/**
 * 发送密码重置邮件
 * 
 * @description 向用户邮箱发送密码重置链接
 * @param email 用户邮箱
 * @returns Promise<ApiResponse<null>>
 * 
 * @example
 * ```typescript
 * const result = await sendPasswordReset('user@example.com');
 * 
 * if (result.success) {
 *   console.log('密码重置邮件已发送');
 * }
 * ```
 */
export async function sendPasswordReset(email: string): Promise<ApiResponse<null>> {
  return post<null>('/api/auth/password/email', { email });
}

/**
 * 重置密码
 * 
 * @description 使用重置令牌重置用户密码
 * @param params 重置密码参数
 * @returns Promise<ApiResponse<null>>
 * 
 * @example
 * ```typescript
 * const result = await resetPassword({
 *   email: 'user@example.com',
 *   token: 'reset-token',
 *   password: 'newpassword123',
 *   password_confirmation: 'newpassword123'
 * });
 * ```
 */
export async function resetPassword(params: {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}): Promise<ApiResponse<null>> {
  return post<null>('/api/auth/password/reset', params);
}

// ============= 验证相关API =============

/**
 * 发送邮箱验证邮件
 * 
 * @description 重新发送邮箱验证邮件
 * @returns Promise<ApiResponse<null>>
 * 
 * @example
 * ```typescript
 * const result = await sendEmailVerification();
 * 
 * if (result.success) {
 *   console.log('验证邮件已发送');
 * }
 * ```
 */
export async function sendEmailVerification(): Promise<ApiResponse<null>> {
  return post<null>('/api/auth/email/verification-notification');
}

/**
 * 验证邮箱
 * 
 * @description 通过验证链接验证用户邮箱
 * @param params 验证参数
 * @returns Promise<ApiResponse<null>>
 * 
 * @example
 * ```typescript
 * const result = await verifyEmail({
 *   id: '123',
 *   hash: 'verification-hash',
 *   expires: '1640995200',
 *   signature: 'signature'
 * });
 * ```
 */
export async function verifyEmail(params: {
  id: string;
  hash: string;
  expires: string;
  signature: string;
}): Promise<ApiResponse<null>> {
  const { id, ...queryParams } = params;
  return get<null>(`/api/auth/email/verify/${id}`, queryParams);
}
